/**
 * Editing one Context entry (SPEC.md §8.13): its row on the Models page, and the edit popup an AI
 * filing opens (D222). Title, text and scope (which AI requests it is read into). The popup
 * also gets Delete and the AI bulb (`editAi`), laid out like the note editor's.
 */

import { useState } from "react";
import type { Area, CaptureTier, ContextEntry, ContextScopeType, Project } from "../api";
import type { AiRefined } from "../aiRefine";
import { describeRefined } from "../aiRefine";
import { scopeOf, scopeValue } from "../contextScope";
import { ContextScopePicker } from "./ContextScopePicker";
import { InboxAiRetryButton } from "./InboxAiRetryButton";
import { useAutoGrow } from "./useAutoGrow";

export type ContextValues = {
  title: string;
  body: string;
  scope_type: ContextScopeType;
  scope_id: string | null;
};

export function EditContextForm({
  entry,
  areas,
  projects,
  onSave,
  onCancel,
  onDelete,
  editAi,
}: {
  entry: ContextEntry;
  areas: Area[];
  projects: Project[];
  onSave: (values: ContextValues) => Promise<void>;
  onCancel: () => void;
  /** Offered in the edit popup, where there is no row with its own Delete beside the form. */
  onDelete?: () => Promise<void>;
  /** The popup's AI bulb: hands over the form as it stands; the form then shows what came back. */
  editAi?: {
    onRetry: (tier: CaptureTier | null, values: ContextValues) => Promise<AiRefined<ContextEntry>>;
    onSettled: (result: AiRefined<ContextEntry> | null) => void | Promise<void>;
  };
}) {
  const [title, setTitle] = useState(entry.title);
  const [body, setBody] = useState(entry.body);
  const [scope, setScope] = useState(scopeValue(entry));
  // Changed since the AI last answered: an answer still showing gives the buttons back.
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useAutoGrow(body);
  const values = (): ContextValues => ({ title: title.trim(), body: body.trim(), ...scopeOf(scope) });
  const changed = <T,>(set: (value: T) => void) => (value: T) => {
    setTouched(true);
    set(value);
  };

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!title.trim() || !body.trim()) return;
    try {
      await onSave(values());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const fold = editAi ? " inbox-fold" : "";
  const save = (
    <button
      className={`btn primary small${fold}`}
      type="submit"
      disabled={!title.trim() || !body.trim()}
    >
      Save
    </button>
  );
  const cancel = (
    <button type="button" className={`btn ghost small${fold}`} onClick={onCancel}>
      Cancel
    </button>
  );

  return (
    <form
      onSubmit={submit}
      style={{ display: "grid", gap: "0.6rem" }}
      data-ai-host={editAi ? "" : undefined}
    >
      {error && <p className="error">{error}</p>}
      <input
        className="input"
        aria-label="Title"
        value={title}
        onChange={(e) => changed(setTitle)(e.target.value)}
        autoFocus
      />
      <textarea
        ref={bodyRef}
        className="input notes"
        aria-label="Body"
        rows={3}
        value={body}
        onChange={(e) => changed(setBody)(e.target.value)}
      />
      <div className="row wrap">
        <ContextScopePicker
          value={scope}
          areas={areas}
          projects={projects}
          onChange={changed(setScope)}
        />
      </div>
      {editAi ? (
        <div className="row wrap">
          {onDelete && (
            <button
              type="button"
              className="btn ghost small danger inbox-fold"
              onClick={() =>
                void onDelete().catch((err: unknown) =>
                  setError(err instanceof Error ? err.message : String(err)),
                )
              }
            >
              Delete
            </button>
          )}
          <span className="ai-spacer" style={{ flex: 1 }} />
          {cancel}
          <InboxAiRetryButton<AiRefined<ContextEntry>>
            surface="context-form"
            label="Tidy up with AI"
            busyLabel="Asking the AI"
            doneLabel="Close - the AI's changes are saved"
            disabled={!title.trim() || !body.trim()}
            keepOpen={touched}
            describe={describeRefined}
            onRetry={async (tier) => {
              setTouched(false);
              const answer = await editAi.onRetry(tier, values());
              setTitle(answer.item.title);
              setBody(answer.item.body);
              setScope(scopeValue(answer.item));
              return answer;
            }}
            onSettled={editAi.onSettled}
            leaveOnClear
          />
          {save}
        </div>
      ) : (
        <div className="row wrap">
          {save}
          {cancel}
        </div>
      )}
    </form>
  );
}
